import { DeckKeyDropdown } from '@/components/deck-shares/DeckKeyDropdown';
import { DeckShareLinkCell } from '@/components/deck-shares/DeckShareLinkCell';
import { HotelDropdown } from '@/components/dropdowns';
import { DateTimePicker, formatDateTime, type CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';
import { z } from 'zod';

/** The standard deck's content key — the default a new share points at. */
export const STANDARD_DECK_KEY = 'deck:standard';

/** Prefix marking a per-prospect deck variant key. */
export const DECK_VARIANT_PREFIX = 'deck:variant:';

/**
 * Turns a deck content key into something a salesperson can read.
 *
 * @param key - A deck content key, e.g. `deck:variant:example-prospect`.
 * @returns `Standard` for the standard deck, otherwise the variant name in
 *   title case.
 */
export function formatDeckKey(key: string | undefined): string {
  if (!key) return '—';
  if (key === STANDARD_DECK_KEY) return 'Standard';
  if (!key.startsWith(DECK_VARIANT_PREFIX)) return key;
  return key
    .slice(DECK_VARIANT_PREFIX.length)
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Zod schemas mirroring the server-side validation in deck-shares.schemas.ts.
 * `token` is absent on purpose: it is minted server-side and never submitted.
 */
export const createDeckShareSchema = z.object({
  recipientName: z.string().min(1).max(200),
  recipientEmail: z.string().email().optional(),
  notes: z.string().max(2000).optional(),
  deckKey: z.string().optional(),
  hotelId: z.string().uuid().optional(),
  isActive: z.boolean().optional(),
  expiresAt: z.string().datetime().optional(),
});

const updateDeckShareSchema = createDeckShareSchema.partial().extend({
  revokedAt: z.string().datetime().nullable().optional(),
});

/** One recorded viewing session of a shared deck. */
export interface DeckShareView {
  id: string;
  viewedAt: string;
  lastSeenAt: string;
  requestCount: number;
  ipAddress?: string | null;
  userAgent?: string | null;
  referrer?: string | null;
}

/** A share link for the presentation, as returned by the API. */
export interface DeckShare {
  id: string;
  recipientName: string;
  recipientEmail?: string | null;
  notes?: string | null;
  deckKey: string;
  token: string;
  hotelId?: string | null;
  isActive: boolean;
  expiresAt?: string | null;
  revokedAt?: string | null;
  viewCount: number;
  firstViewedAt?: string | null;
  lastViewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present on the create response — the link to send. */
  shareUrl?: string;
  // Virtual column: the link + copy button (generated, not stored).
  shareLink?: never;
  hotel?: {
    id: string;
    name: string;
    city: string;
    state: string;
  };
  createdBy?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  views?: DeckShareView[];
}

/** Presentation share CRUD configuration. */
export const deckShareCrudConfig: CrudConfig<DeckShare> = {
  entityName: 'Presentation Share',
  entityNamePlural: 'Presentation Shares',

  endpoints: {
    list: '/deck-shares',
    single: (id: string) => `/deck-shares/${id}`,
    create: '/deck-shares',
    update: (id: string) => `/deck-shares/${id}`,
    delete: (id: string) => `/deck-shares/${id}`,
  },

  fields: [
    {
      key: 'recipientName',
      label: 'Sent to',
      type: 'text',
      required: true,
      // A format hint, not a sample person: a placeholder naming a real
      // prospect and a real property reads as live data to whoever opens the
      // form next.
      placeholder: 'Contact name — Hotel name',
      validation: { min: 1, max: 200 },
    },
    {
      key: 'recipientEmail',
      label: 'Recipient Email',
      type: 'text',
      required: false,
      placeholder: 'name@hotel.com',
    },
    {
      key: 'shareLink',
      label: 'Link',
      type: 'custom',
      required: false,
      format: (_value: unknown, fullRecord?: DeckShare) => (
        <DeckShareLinkCell share={fullRecord} />
      ),
    },
    {
      key: 'deckKey',
      label: 'Deck',
      type: 'custom',
      required: false,
      defaultValue: STANDARD_DECK_KEY,
      format: (value: unknown) =>
        formatDeckKey(typeof value === 'string' ? value : undefined),
      inputFormat: (value: unknown, onChange: (next: string) => void) => (
        <DeckKeyDropdown
          value={typeof value === 'string' ? value : STANDARD_DECK_KEY}
          onChange={onChange}
        />
      ),
    },
    {
      key: 'viewCount',
      label: 'Views',
      type: 'number',
      readonly: true,
      // A zero here is the answer to "have they looked at it?" — show it as a
      // real zero, not an em dash, or "not yet opened" reads as "no data".
      format: (value: unknown) => (typeof value === 'number' ? String(value) : '0'),
    },
    {
      key: 'lastViewedAt',
      label: 'Last viewed',
      type: 'datetime',
      readonly: true,
      format: (value: unknown) =>
        typeof value === 'string' ? formatDateTime(value) : 'Not yet opened',
    },
    {
      key: 'firstViewedAt',
      label: 'First viewed',
      type: 'datetime',
      readonly: true,
      format: (value: unknown) =>
        typeof value === 'string' ? formatDateTime(value) : 'Not yet opened',
    },
    {
      key: 'hotel',
      label: 'Hotel',
      type: 'text',
      required: false,
      readonly: true,
      format: (value: { id: string; name: string } | undefined) =>
        value ? (
          <Link className="text-primary" to={`/hotels/${value.id}`}>
            {value.name}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'hotelId',
      label: 'Hotel',
      type: 'custom',
      required: false,
      placeholder: 'Select Hotel (optional)',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => <HotelDropdown value={value} onChange={onChange} placeholder={field.placeholder} />,
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'textarea',
      required: false,
      placeholder: 'What was pitched, which pricing was quoted…',
      validation: { max: 2000 },
    },
    {
      key: 'isActive',
      label: 'Link Active',
      type: 'boolean',
      required: false,
      defaultValue: true,
    },
    {
      key: 'expiresAt',
      label: 'Expires At',
      type: 'datetime',
      required: false,
      inputFormat: (value: unknown, onChange: (next: string | undefined) => void) => (
        <DateTimePicker
          date={typeof value === 'string' ? value : undefined}
          onDateTimeChange={(date) => onChange(date?.toISOString())}
          dateLabel=""
          timeLabel=""
        />
      ),
    },
    {
      key: 'revokedAt',
      label: 'Revoked At',
      type: 'datetime',
      required: false,
      readonly: true,
    },
    { key: 'createdAt', label: 'Created At', type: 'datetime', readonly: true },
    { key: 'updatedAt', label: 'Updated At', type: 'datetime', readonly: true },
  ],

  listView: {
    fields: ['recipientName', 'deckKey', 'viewCount', 'lastViewedAt', 'shareLink'],
    // Without an explicit `create` action neither the header button nor the
    // empty-state button renders (CrudListHeader gates both on
    // `actions.create.enabled`) — the /new route would exist with nothing
    // linking to it, which is no use to the person who needs a link.
    // The URL is omitted on purpose: it defaults to `${baseUrl}/new`.
    actions: {
      create: { enabled: true, label: 'New Share Link' },
    },
    searchableFields: ['recipientName', 'recipientEmail', 'notes'],
    sortableFields: ['recipientName', 'viewCount', 'lastViewedAt', 'createdAt'],
    // Newest link first, NOT most-recently-viewed. `lastViewedAt` is null until
    // a prospect opens the link, and Postgres sorts nulls as the largest value —
    // `lastViewedAt DESC` would therefore float every never-opened share to the
    // top and bury the ones that were just read. Sorting by "Views" is one
    // column click away when that's the question.
    defaultSort: { field: 'createdAt', direction: 'desc' },
    itemsPerPage: 25,
    filters: [],
  },

  detailView: {
    sections: [
      {
        title: 'Recipient',
        description: 'Who this link was created for',
        fields: ['recipientName', 'recipientEmail', 'hotel', 'notes'],
        defaultExpanded: true,
      },
      {
        title: 'Activity',
        description: 'When the presentation was opened',
        fields: ['viewCount', 'firstViewedAt', 'lastViewedAt'],
        defaultExpanded: true,
      },
      {
        title: 'Link',
        description: 'Which deck it serves and how long it stays live',
        fields: ['deckKey', 'isActive', 'expiresAt', 'revokedAt'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    createSchema: createDeckShareSchema,
    updateSchema: updateDeckShareSchema,
    sections: [
      {
        title: 'Recipient',
        description: 'Who you are sending the presentation to',
        fields: ['recipientName', 'recipientEmail', 'hotelId', 'notes'],
        columns: 1,
      },
      {
        title: 'Link',
        description: 'Which deck the link opens, and when it stops working',
        fields: ['deckKey', 'isActive', 'expiresAt'],
        columns: 2,
      },
    ],
  },
};
